import React, { ReactNode, useCallback, useState } from 'react';
import { Drawer, Stack } from '@moda/om';
import { ErrorLike } from '@apollo/client';
import { noop } from '@moda/portal-stanchions';
import { SortOption } from '../../constants';
import { tc } from '../../lib/trackingContext';
import { SidebarLayout, Sidebar, Content } from '../SidebarLayout';
import { PageFilter, PageFilterClear, useActivePageFilters } from '../PageFilter';
import { GridItemCount } from '../GridPeripherals/GridItemCount';
import { GridSortByDropdown } from '../GridPeripherals/GridSortByDropdown';
import { SalePercentageTags } from '../SalePercentageTags/SalePercentageTags';
import { PLPBreadcrumbs } from '../PLPBreadcrumbs';
import { GuidePageQuery, ProductListPageResultFragment } from '../../generated/types';
import { ProductListingFilterToggle } from './ProductListingFilterToggle';

import './ProductListingDesktop.scss';

type DisplayPage = NonNullable<GuidePageQuery['displayPage']>;

type Props = {
  currentSort: SortOption;
  onSortChanged: (sort: string) => void;
  availableSorts: SortOption[];
  headerText?: string;
  error?: ErrorLike;
  description?: string;
  showPLPBreadcrumbs?: boolean;
  preserveBreadcrumbSpace?: boolean;
  hideControls?: boolean;
  displayPage?: DisplayPage | null;
  queryResult?: ProductListPageResultFragment | null;
  belowHeader?: ReactNode;
  belowLayout?: ReactNode;
  biggerImagesOnPLP?: boolean;
  children?: React.ReactNode;
};

export const ProductListingDesktop: React.FC<Props> = ({
  children,
  headerText,
  currentSort,
  queryResult,
  displayPage,
  onSortChanged,
  availableSorts,
  showPLPBreadcrumbs,
  preserveBreadcrumbSpace,
  hideControls,
  belowHeader,
  belowLayout,
  biggerImagesOnPLP
}) => {
  const totalItems = queryResult?.totalItems || 0;
  const [filterOpen, setFilterOpen] = useState(false);
  const toggleFilter = useCallback(() => {
    tc.track(filterOpen ? 'Filters Closed' : 'Filters Opened');
    setFilterOpen(open => !open);
  }, [filterOpen]);

  const activeFilters = useActivePageFilters({
    attributes: queryResult?.attributes || [],
    designers: queryResult?.designers || [],
    sizes: queryResult?.sizes || [],
    categoryTree: queryResult?.categoryTree || null,
    includeCategories: true
  });

  const filterFacets = (
    <PageFilter
      attributes={queryResult?.attributes || []}
      availability={queryResult?.availability || null}
      categoryTree={queryResult?.categoryTree || null}
      colors={queryResult?.filterColors || []}
      designers={queryResult?.designers || []}
      saleTags={queryResult?.saleTags || []}
      sizes={queryResult?.sizes || []}
      guideSubPages={displayPage?.guideSubPages || null}
      onClose={noop}
    />
  );

  return (
    <>
      {showPLPBreadcrumbs ? (
        <PLPBreadcrumbs />
      ) : preserveBreadcrumbSpace ? (
        <div className="ProductListingDesktop__breadcrumbs-placeholder" aria-hidden>
          <PLPBreadcrumbs />
        </div>
      ) : null}

      <h1 className="ProductListingDesktop__header" data-testid="ProductListingDesktop__header">
        {headerText}
      </h1>

      {belowHeader}

      <SalePercentageTags markdownPercentages={queryResult?.markdownPercentages || []} />

      {!hideControls && (
        <Stack
          space={4}
          direction="horizontal"
          justifyContent={biggerImagesOnPLP ? 'space-between' : 'flex-end'}
          alignItems="center"
          className="ProductListingDesktop__grid-container"
        >
          {biggerImagesOnPLP && (
            <ProductListingFilterToggle
              open={filterOpen}
              activeFilterCount={activeFilters.length}
              onClick={toggleFilter}
            />
          )}

          <Stack space={4} direction="horizontal" alignItems="center">
            <GridItemCount count={totalItems} />

            <GridSortByDropdown
              currentSort={currentSort}
              availableSorts={availableSorts}
              onSortByChange={option => onSortChanged(option)}
            />
          </Stack>
        </Stack>
      )}

      <SidebarLayout>
        {!hideControls &&
          (biggerImagesOnPLP ? (
            <Drawer open={filterOpen}>{filterFacets}</Drawer>
          ) : (
            <Sidebar stickAt={150}>{filterFacets}</Sidebar>
          ))}

        <Content>
          <Stack space={3}>
            <PageFilterClear
              attributes={queryResult?.attributes || []}
              designers={queryResult?.designers || []}
              sizes={queryResult?.sizes || []}
              categoryTree={queryResult?.categoryTree || null}
              showCategoryFilters={biggerImagesOnPLP}
            />
            {children}
          </Stack>
        </Content>
      </SidebarLayout>

      {belowLayout}
    </>
  );
};
